import {Component, ElementRef, OnInit, ViewChild, ChangeDetectorRef} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {FixedAssetReportsApiService, DisposalsFilters, DisposalRowItem} from '../../../../Services/fixed-asset-reports-api.service';
import {FixedAssetModel} from '../../../../DataModels/fixed-asset-model';
import {DataExportService} from '../../../../Services/dataExport.service';
import {NotificationService} from '../../../../Services/notification.service';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';
import {DataService} from '../../../../Services/data.service';
import {ResponseModel} from '../../../../DataModels/response-model';
import {ReportPrintService} from '../../../../Services/report-print.service';

@Component({
  selector: 'app-disposals-report',
  templateUrl: './disposals.component.html',
  styleUrls: ['./disposals.component.css'],
  standalone: false
})
export class DisposalsReportComponent implements OnInit {
  disposed: { name: string; disposal?: { disposalDate?: string; proceeds?: number; gainLoss?: number } }[] = [];
  vm: any = {
    showFilters: false,
    filtered: false,
    buttonsDisabled: false,
    report: [],
    report_format: false,
    monthCount: 0,
    showSpecificDates: false,
    company: [],
    location: [],
    request: {
      fromDate: null,
      toDate: null,
      company_id: '',
      location_id: ''
    },
  };
  buttonConfig: any = {
    reportFormat: false,
    // single print button
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: true,
    close: true,
  };
  @ViewChild('tableRef') tableRef?: ElementRef<HTMLTableElement>;
  @ViewChild('printArea') printArea?: ElementRef<HTMLElement>;
  reportEmail: EmailModel = new EmailModel();

  constructor(
    private title: Title,
    private reportsApi: FixedAssetReportsApiService,
    private dataExport: DataExportService,
    private notify: NotificationService,
    private cdr: ChangeDetectorRef,
    private dateFilter: DateFilterService,
    private tableDataService: DataService,
    private reportPrint: ReportPrintService,
  ) {
  }

  ngOnInit(): void {
    this.title.setTitle($localize`:Disposals@@disposals:Disposals`);
    // initialize from/to date lookups similar to Trial Balance
    try {
      const ret = this.dateFilter.DateFilterInit(this.vm.request.toDate, this.vm.request.fromDate);
      (this.vm as any).fromDateSelect = ret.fromDateSelect;
      (this.vm as any).toDateSelect = ret.toDateSelect;
      this.vm.monthCount = ret.monthCount;
      const defaults = this.dateFilter.GetDefaultDateSpanRange(ret.fromDateSelect, ret.toDateSelect, 90);
      this.vm.request.fromDate = defaults.fromDate;
      this.vm.request.toDate = defaults.toDate;
    } catch {
    }
    // Populate Company and Location lookups
    try {
      this.tableDataService.getData('company?state=active&pageNumber=0&pageSize=999999')
        .subscribe((result: ResponseModel) => {
          (this.vm as any).company = (result as any)?.Data ?? result;
        });
      this.tableDataService.getData('location?state=active&pageNumber=0&pageSize=999999')
        .subscribe((result: ResponseModel) => {
          (this.vm as any).location = (result as any)?.Data ?? result;
        });
    } catch {
    }
    this.fetchDisposals();
  }

  update(): void {
    this.vm.filtered = true;
    this.fetchDisposals();
  }

  clear(): void {
    this.vm.filtered = false;
    const defaults = this.dateFilter.GetDefaultDateSpanRange(this.vm.fromDateSelect, this.vm.toDateSelect, 90);
    this.vm.request = {
      fromDate: defaults.fromDate,
      toDate: defaults.toDate,
      company_id: '',
      location_id: ''
    };
    this.fetchDisposals();
  }

  close(): void {
  }

  onPrint(): void {
    const reportName = $localize`:Disposals@@disposals:Disposals`;
    const rawHtml = this.printArea?.nativeElement?.outerHTML || this.tableRef?.nativeElement?.outerHTML || '';
    const content = this.reportPrint.cleanContentHtml(rawHtml);
    const html = this.reportPrint.buildHtml({title: reportName, contentHtml: content, orientation: 'landscape'});
    this.reportPrint.printInIframe(html);
  }

  onExport(): void {
    const el = this.tableRef?.nativeElement;
    if (el) {
      this.dataExport.exportToExcel(el, 'disposals.xlsx', 'Disposals');
    } else {
      this.notify.bsWarning($localize`:Nothing to export@@nothingToExport:Nothing to export`);
    }
  }

  onEmail(): void {
    const emailDocObj = new EmailModel();
    const reportName = $localize`:Disposals@@disposals:Disposals`;

    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = $localize`:EmailBody@@emailBody:Please see the attached ${reportName}.` +
      '<br><br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + reportName;

    try {
      this.cdr.detectChanges();
    } catch {
    }
    try {
      const html = this.printArea?.nativeElement?.outerHTML || this.tableRef?.nativeElement?.outerHTML || '';
      emailDocObj.pdfHtml = this.reportPrint.buildHtml({
        title: reportName,
        contentHtml: this.reportPrint.cleanContentHtml(html),
        orientation: 'landscape'
      });
    } catch {
      emailDocObj.pdfHtml = '';
    }

    const now = new Date();
    const yyyy = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    emailDocObj.FileName = `Disposals_${yyyy}${mm}${dd}.pdf`;
    emailDocObj.activity_message = reportName;
    this.reportEmail = emailDocObj;

    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      const emailModal = new Modal(element);
      // Re-enable header buttons after email modal closes
      try {
        element.addEventListener('hidden.bs.modal', () => {
          this.vm.buttonsDisabled = false;
          this.vm.report = this.disposed && this.disposed.length ? this.disposed : [{}];
        }, {once: true});
      } catch {
      }
      emailModal.show();
    }
  }


  private fetchDisposals(): void {
    const page = 1;
    const pageSize = 100000; // large page to mimic current no-paging UI
    const f: DisposalsFilters = {
      fromDate: this.vm.request.fromDate,
      toDate: this.vm.request.toDate,
      companyId: this.vm.request.company_id,
      locationId: this.vm.request.location_id,
    };
    this.reportsApi.getDisposals(page, pageSize, f).subscribe({
      next: ({items}) => {
        this.disposed = (items || []).map(r => ({
          name: r.name,
          disposal: {
            disposalDate: r.disposalDate,
            proceeds: r.proceeds,
            gainLoss: r.gainLoss,
          }
        }));
        this.vm.report = this.disposed && this.disposed.length ? this.disposed : [{}];
        try {
          this.cdr.detectChanges();
        } catch {
        }
      },
      error: (err) => {
        try {
          console.log('[Disposals][fetch error]', err);
        } catch {
        }
        this.disposed = [];
        this.vm.report = [];
        try {
          this.cdr.detectChanges();
        } catch {
        }
      }
    });
  }
}
